import React, { useState, useEffect } from 'react'
import {
    DataGrid,
    useGridApiContext,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    GridToolbarExport,
    GridToolbarExportContainer,
    GridCsvExportMenuItem,
    GridToolbarDensitySelector,
    GridToolbarQuickFilter,
    GridActionsCellItem,
} from '@mui/x-data-grid'
import { Box, Button, IconButton, Tooltip } from '@mui/material'
import './CustomDataGrid.css'
import moment from 'moment'
import { styled } from '@mui/material/styles'
import DeleteIcon from '@mui/icons-material/Delete'
import {
    AccessTime as AccessTimeIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
} from '@mui/icons-material'

import {
    capitalize as capitalized,
    handleRedirectInternal,
} from '@/product/common/components'
import { currencyFormat } from '@/custom/common/components'
import { useHistory } from 'react-router-dom'
import Snackbar from '@mui/material/Snackbar'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'

import Alert from '@mui/material/Alert'

const useFakeMutation = () => {
    return React.useCallback(
        (user) =>
            new Promise((resolve, reject) => {
                setTimeout(() => {
                    resolve(user)
                }, 200)
            }),
        []
    )
}

function computeMutation(newRow, oldRow) {
    const updatedField = Object.keys(newRow).find(
        (key) => newRow[key] !== oldRow[key]
    )

    const updatedValue = newRow[updatedField]
    return `the value from '${oldRow[updatedField]}' to '${newRow[updatedField]}'`
    // console.log('useFakeMutation newRow, oldRow', newRow, oldRow)
    // return `Sure you want to change the value`
}

const StyledGridOverlay = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '100px',
    paddingBottom: '20px',
    height: '100%',
    '& .ant-empty-img-1': {
        fill: theme.palette.mode === 'light' ? '#aeb8c2' : '#262626',
    },
    '& .ant-empty-img-2': {
        fill: theme.palette.mode === 'light' ? '#f5f5f7' : '#595959',
    },
    '& .ant-empty-img-3': {
        fill: theme.palette.mode === 'light' ? '#dce0e6' : '#434343',
    },
    '& .ant-empty-img-4': {
        fill: theme.palette.mode === 'light' ? '#fff' : '#1c1c1c',
    },
    '& .ant-empty-img-5': {
        fillOpacity: theme.palette.mode === 'light' ? '0.8' : '0.08',
        fill: theme.palette.mode === 'light' ? '#f5f5f5' : '#fff',
    },
}))

const isValidHttpUrl = (string) => {
    let url

    try {
        url = new URL(string)
    } catch (_) {
        return false
    }

    return url.protocol === 'http:' || url.protocol === 'https:'
}

function NoRecords() {
    return (
        <StyledGridOverlay>
            <svg
                width="120"
                height="100"
                viewBox="0 0 184 152"
                aria-hidden
                focusable="false"
            >
                <g fill="none" fillRule="evenodd">
                    <g transform="translate(24 31.67)">
                        <ellipse
                            className="ant-empty-img-5"
                            cx="67.797"
                            cy="106.89"
                            rx="67.797"
                            ry="12.668"
                        />
                        <path
                            className="ant-empty-img-1"
                            d="M122.034 69.674L98.109 40.229c-1.148-1.386-2.826-2.225-4.593-2.225h-51.44c-1.766 0-3.444.839-4.592 2.225L13.56 69.674v15.383h108.475V69.674z"
                        />
                        <path
                            className="ant-empty-img-2"
                            d="M33.83 0h67.933a4 4 0 0 1 4 4v93.344a4 4 0 0 1-4 4H33.83a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z"
                        />
                        <path
                            className="ant-empty-img-3"
                            d="M42.678 9.953h50.237a2 2 0 0 1 2 2V36.91a2 2 0 0 1-2 2H42.678a2 2 0 0 1-2-2V11.953a2 2 0 0 1 2-2zM42.94 49.767h49.713a2.262 2.262 0 1 1 0 4.524H42.94a2.262 2.262 0 0 1 0-4.524zM42.94 61.53h49.713a2.262 2.262 0 1 1 0 4.525H42.94a2.262 2.262 0 0 1 0-4.525zM121.813 105.032c-.775 3.071-3.497 5.36-6.735 5.36H20.515c-3.238 0-5.96-2.29-6.734-5.36a7.309 7.309 0 0 1-.222-1.79V69.675h26.318c2.907 0 5.25 2.448 5.25 5.42v.04c0 2.971 2.37 5.37 5.277 5.37h34.785c2.907 0 5.277-2.421 5.277-5.393V75.1c0-2.972 2.343-5.426 5.25-5.426h26.318v33.569c0 .617-.077 1.216-.221 1.789z"
                        />
                    </g>
                    <path
                        className="ant-empty-img-3"
                        d="M149.121 33.292l-6.83 2.65a1 1 0 0 1-1.317-1.23l1.937-6.207c-2.589-2.944-4.109-6.534-4.109-10.408C138.802 8.102 148.92 0 161.402 0 173.881 0 184 8.102 184 18.097c0 9.995-10.118 18.097-22.599 18.097-4.528 0-8.744-1.066-12.28-2.902z"
                    />
                    <g
                        className="ant-empty-img-4"
                        transform="translate(149.65 15.383)"
                    >
                        <ellipse cx="20.654" cy="3.167" rx="2.849" ry="2.815" />
                        <path d="M5.698 5.63H0L2.898.704zM9.259.704h4.985V5.63H9.259z" />
                    </g>
                </g>
            </svg>
            <Box
                style={{
                    marginTop: '10px',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#48535e',
                }}
            >
                No Records Found
            </Box>
        </StyledGridOverlay>
    )
}

const returnSelectedRows = (apiRef, props, rowData) => {
    let selectedValues = []
    let arrayValue = []
    if (props.diff_id || rowData?.diff_id) {
        let selected = Array.from(apiRef?.current?.getSelectedRows().values())
        if (selected.length) {
            selected.map((data) => {
                if (rowData?.clickID && data[rowData?.clickID]) {
                    arrayValue.push(data[rowData?.clickID])
                } else {
                    arrayValue.push(data.id)
                }
            })
        }
    } else {
        arrayValue = Array.from(apiRef?.current?.getSelectedRows().keys())
    }
    if (arrayValue.length) {
        selectedValues = arrayValue
    }

    return selectedValues
}

const csvOptions = { delimiter: ';' }

function CustomExportButton(props) {
    return (
        <GridToolbarExportContainer {...props}>
            <GridCsvExportMenuItem
                options={{
                    fileName: 'Export Data',

                    utf8WithBom: true,
                }}
            />
        </GridToolbarExportContainer>
    )
}

function CustomToolbar({ props }) {
    const apiRef = useGridApiContext()
    let customToolbarButton = props.customToolbarButton
    return (
        <GridToolbarContainer>
            <GridToolbarColumnsButton />

            {props.tableValues.some((item) => item.filterable === true) ? (
                <GridToolbarFilterButton />
            ) : null}

            <GridToolbarDensitySelector />
            <CustomExportButton />
            <Button
                className="ml-auto customToolbarBtn"
                onClick={customToolbarButton?.function}
            >
                <span className="material-icons">
                    {customToolbarButton?.icon}
                </span>
                {customToolbarButton?.label}
            </Button>
            {/* <GridToolbarQuickFilter /> */}
            {props.tableActions?.length ? (
                <div className="toolbarActions">
                    {apiRef.current.getSelectedRows().size
                        ? props.tableActions?.map((data, indexhead) => {
                              return (
                                  <Tooltip title={data.label} arrow>
                                      <IconButton
                                          id={data.id ? data.id : data.label}
                                          onClick={() => {
                                              data.onclick(
                                                  returnSelectedRows(
                                                      apiRef,
                                                      props,
                                                      data
                                                  ),
                                                  data.type
                                              )
                                          }}
                                          aria-label={data.label}
                                          size="large"
                                      >
                                          {data.icon}
                                      </IconButton>
                                  </Tooltip>
                              )
                          })
                        : null}
                </div>
            ) : null}
        </GridToolbarContainer>
    )
}

const calculateSummary = (columns, apiSummary) => {
    if (!apiSummary || !columns.length) {
        return {}
    }

    return columns.reduce((acc, col) => {
        if (col.field && col.summaryType) {
            const fieldName = `${col.field}_${col.summaryType}_summary`
            acc[col.field] = apiSummary.hasOwnProperty(fieldName)
                ? apiSummary[fieldName]
                : ''
        }
        return acc
    }, {})
}

function CustomDataGrid({ props }) {
    const history = useHistory()
    const formik = props?.formik
    const changeTableFunction = props?.changeTableFunction

    const handleChangeRowsPerPage = (event) => {
        formik.limit = event.pageSize
        formik.page = 1
        changeTableFunction(
            formik,
            props.fromVariable ? props.fromVariable : ''
        )
    }

    const [rowSelectionModel, setRowSelectionModel] = React.useState([])

    const handleChangePage = (event) => {
        if (event.page == formik.page - 1 || event.page == 0) {
            handleChangeRowsPerPage(event)
        } else {
            if (formik.limit != event.pageSize && formik.page != event.page) {
                formik.page = 1
                formik.limit = event.pageSize
            } else {
                formik.page = event.page + 1
            }
            changeTableFunction(
                formik,
                props.fromVariable ? props.fromVariable : ''
            )
        }
    }

    const handlePageChange = (event) => {
        setPaginationModel(event)
    }

    var evaluate = (c) => Function(`return ${c}`)()

    const actionCondition = (row, valhead) => {
        let returnValue = true

        if (valhead.condition) {
            if (valhead.conditionValue) {
                let condition = valhead.conditionValue
                if (valhead.conditionValue.formula.isString) {
                    if (valhead.conditionValue.formula.notation == 'includes') {
                        if (
                            condition.formula.key2.includes(
                                row[condition.formula.key1]
                            )
                        ) {
                            returnValue = true
                        } else {
                            returnValue = false
                        }
                    } else {
                        if (
                            evaluate(
                                `"${row[condition.formula.key1]}" ${
                                    condition.formula.notation
                                } "${condition.formula.key2}"`
                            )
                        ) {
                            returnValue = true
                        } else {
                            returnValue = false
                        }
                    }
                } else {
                    if (valhead.conditionValue.formula.notation == 'includes') {
                        if (
                            condition.formula.key2.includes(
                                row[condition.formula.key1]
                            )
                        ) {
                            returnValue = true
                        } else {
                            returnValue = false
                        }
                    } else {
                        if (
                            evaluate(
                                `${row[condition.formula.key1]} ${
                                    condition.formula.notation
                                } ${condition.formula.key2}`
                            )
                        ) {
                            returnValue = true
                        } else {
                            returnValue = false
                        }
                    }
                }
            }
        }
        return returnValue
    }

    const conditionalFormatting = (row, valhead) => {
        let returnValue = row[valhead.field] || '-'
        if (
            valhead.condition &&
            valhead.conditionValue &&
            valhead.conditionValue.formula.isString
        ) {
            let condition = valhead.conditionValue
            if (
                evaluate(
                    `"${row[condition.formula.key1]}" ${
                        condition.formula.notation
                    } "${condition.formula.key2}"`
                )
            ) {
                if (condition.true.type === 'text') {
                    returnValue = condition.true.value
                } else if (condition.true.type === 'key') {
                    returnValue = row[condition.true.value]
                }
            } else {
                if (condition.false.type === 'text') {
                    returnValue = condition.false.value
                } else if (condition.false.type === 'key') {
                    returnValue = row[condition.false.value]
                }
            }
        } else if (valhead.condition) {
            if (valhead.conditionValue) {
                let condition = valhead.conditionValue

                if (
                    evaluate(
                        `${row[condition.formula.key1]} ${
                            condition.formula.notation
                        } ${condition.formula.key2}`
                    )
                ) {
                    if (condition.true.type === 'text') {
                        returnValue = condition.true.value
                    } else if (condition.true.type === 'key') {
                        returnValue = row[condition.true.value]
                    } else if (condition.true.type === 'icon') {
                        returnValue = (
                            <Button id="actionIcon">
                                <span className="material-icons">
                                    {condition.true.icon_name}
                                </span>
                            </Button>
                        )
                    }
                } else {
                    if (condition.false.type === 'text') {
                        returnValue = condition.false.value
                    } else if (condition.false.type === 'key') {
                        returnValue = row[condition.false.value]
                    } else if (condition.false.type === 'icon') {
                        returnValue = (
                            <Button id="conditionIcon">
                                <span className="material-icons">
                                    {condition.false.icon_name}
                                </span>
                            </Button>
                        )
                    }
                }
            }
        }
        return returnValue
    }

    function renderTooltipCell(configItem, clickID, secondaryClickID) {
        const CellWithTooltip = (params) => {
            let tooltipText = params.value || ''

            if (configItem.disableTooltip && params?.row?.description) {
                const desc = params.row.description || ''
                const hasReason = /reason:/i.test(desc)
                let mainMessage = desc
                let cleanedParts = []
                if (hasReason) {
                    const parts = desc.split(',')
                    mainMessage = desc.split(/reason:/i)[0].trim()
                    cleanedParts = parts.map((item) => item.trim())
                    cleanedParts[0] = cleanedParts[0].replace(
                        /.*Reason:\s*/i,
                        ''
                    )
                }
                if (hasReason) {
                    if (configItem.tooltipFormat === 'number') {
                        tooltipText = `${mainMessage}\nReason:\n${cleanedParts
                            .map((item, i) => `${i + 1}. ${item}`)
                            .join('\n')}`
                    } else {
                        tooltipText = `${mainMessage}\nReason: ${cleanedParts.join(
                            ', '
                        )}`
                    }
                } else {
                    tooltipText = mainMessage
                }
            }

            return params.row.id !== 'total-row' ? (
                <Tooltip
                    title={
                        <span style={{ whiteSpace: 'pre-line' }}>
                            {tooltipText}
                        </span>
                    }
                    arrow
                >
                    <span
                        className="MuiDataGrid-cellContent"
                        onClick={() =>
                            configItem.onclick &&
                            configItem.onclick(
                                clickID ? params?.row[clickID] : params?.row.id,
                                secondaryClickID
                                    ? params?.row[secondaryClickID]
                                    : undefined,
                                configItem.clickType
                            )
                        }
                    >
                        {params.value}
                    </span>
                </Tooltip>
            ) : null
        }

        CellWithTooltip.displayName = 'CellWithTooltip'
        return CellWithTooltip
    }

    function convertConfig(inputConfig) {
        const outputConfig = []

        inputConfig.forEach((configItem) => {
            const {
                field,
                label,
                type,
                countryfield,
                firstChild,
                clickID,
                secondaryClickID,
                sort,
                hideable,
                sortField,
                flex,
                width,
                isMultiple,
                filterable,
                editable,
                condition,
                conditionValue,
                filterField,
                valueOptions,
                alignTitle,
                alignContent,
                tooltipTitle,
                redirectType,
                capitalize,
                addStarMark,
                summary,
                summaryType,
                summaryField,
                valueGetter,
            } = configItem

            let headerName = label
            let sortable = sort
            if (label === 'ID') headerName = 'ID'
            if (label === 'Picture') headerName = 'Picture'
            if (label === 'Title') headerName = 'Title'
            if (label === 'Updated At') headerName = 'Updated At'
            if (label === 'Posted By') headerName = 'Posted By'

            const count = isMultiple?.filter(
                (obj) =>
                    obj.showInMenu === false || obj.showInMenu === undefined
            ).length

            const actionsWidth =
                typeof count === 'number' ? count * 60 + 60 : 100

            const newConfigItem = {
                field,
                headerName,
                type,
                sortable,
                condition,
                conditionValue,
                hideable: hideable || true,
                capitalize: capitalize || false,
                // flex: 1,
                // width: width || 120,
                isMultiple: isMultiple ? isMultiple.length : 0,
            }

            newConfigItem.headerAlign = alignTitle || 'left'
            newConfigItem.align = alignContent || 'left'

            // Added valueGetter
            if (valueGetter) {
                newConfigItem.valueGetter = valueGetter
            }

            if (type === 'imagewithurl' && !firstChild) {
                newConfigItem.renderCell = (params) =>
                    params.row.id !== 'total-row' && (
                        <img
                            src={
                                params.value && isValidHttpUrl(params.value)
                                    ? params.value
                                    : params.value &&
                                        isValidHttpUrl(
                                            `${
                                                global.customImgurl
                                                    ? process.env
                                                          .REACT_APP_IMAGE_URL
                                                    : process.env
                                                          .REACT_APP_DOMAIN
                                            }/uploads/${configItem.folder}/${
                                                params.value
                                            }`
                                        )
                                      ? `${
                                            global.customImgurl
                                                ? process.env
                                                      .REACT_APP_IMAGE_URL
                                                : process.env.REACT_APP_DOMAIN
                                        }/uploads/${configItem.folder}/${
                                            params.value
                                        }`
                                      : params.row['avatar'] &&
                                          isValidHttpUrl(params.row['avatar'])
                                        ? params.row['avatar']
                                        : params.row['avatar'] &&
                                            isValidHttpUrl(
                                                `${
                                                    global.customImgurl
                                                        ? process.env
                                                              .REACT_APP_IMAGE_URL
                                                        : process.env
                                                              .REACT_APP_DOMAIN
                                                }/uploads/${configItem.folder}/${
                                                    params.row['avatar']
                                                }`
                                            )
                                          ? `${
                                                global.customImgurl
                                                    ? process.env
                                                          .REACT_APP_IMAGE_URL
                                                    : process.env
                                                          .REACT_APP_DOMAIN
                                            }/uploads/${configItem.folder}/${
                                                params.row['avatar']
                                            }`
                                          : '/assets/images/noImg.png'
                            }
                            onClick={() => {
                                if (configItem?.onclick) {
                                    configItem.onclick(
                                        clickID
                                            ? params?.row[clickID]
                                            : params?.row.id,
                                        secondaryClickID
                                            ? params?.row[secondaryClickID]
                                            : params?.row[secondaryClickID],
                                        configItem.clickType
                                    )
                                }
                            }}
                            alt={params?.row.title}
                            style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '6px',
                                objectFit: 'cover',
                            }}
                            onError={(e) =>
                                (e.target.src = '/assets/images/noImg.png')
                            }
                        />
                    )
                newConfigItem.filterable = false
            }

            if (type === 'multiselect' && !firstChild) {
                newConfigItem.renderCell = (stringurl) => {
                    let displayArray = []

                    if (stringurl && stringurl?.value && valueOptions) {
                        stringurl?.value
                            .toString()
                            .split(',')
                            .map((params, index) => {
                                displayArray.push(
                                    `${
                                        valueOptions.filter(
                                            (value) =>
                                                value.value.toString() ===
                                                params.toString()
                                        )[0]?.label
                                    }`
                                )
                                return true
                            })
                    }

                    return <>{displayArray.toString()}</>
                }
            }

            if (type === 'multiimagewithurl' && !firstChild) {
                newConfigItem.renderCell = (imageurl) => {
                    return (
                        <>
                            {imageurl && imageurl?.value
                                ? imageurl?.value
                                      .toString()
                                      .split(',')
                                      .map((params, index) => {
                                          return (
                                              <img
                                                  src={
                                                      isValidHttpUrl(
                                                          `${
                                                              process.env
                                                                  .REACT_APP_IMAGE_URL
                                                          }${
                                                              configItem.folder ||
                                                              'product'
                                                          }/${params}`
                                                      )
                                                          ? `${
                                                                process.env
                                                                    .REACT_APP_IMAGE_URL
                                                            }${
                                                                configItem.folder ||
                                                                'product'
                                                            }/${params}`
                                                          : isValidHttpUrl(
                                                                  params
                                                              )
                                                            ? params
                                                            : isValidHttpUrl(
                                                                    `${
                                                                        process
                                                                            .env
                                                                            .REACT_APP_DOMAIN
                                                                    }/uploads/${
                                                                        configItem.folder ||
                                                                        'product'
                                                                    }/${params}`
                                                                )
                                                              ? `${
                                                                    process.env
                                                                        .REACT_APP_DOMAIN
                                                                }/uploads/${
                                                                    configItem.folder ||
                                                                    'product'
                                                                }/${params}`
                                                              : '/assets/images/noImg.png'
                                                  }
                                                  alt={''}
                                                  style={{
                                                      width: '40px',
                                                      height: '40px',
                                                      borderRadius: '6px',
                                                      objectFit: 'cover',
                                                  }}
                                                  onError={(e) =>
                                                      (e.target.src =
                                                          '/assets/images/noImg.png')
                                                  }
                                              />
                                          )
                                      })
                                : null}
                        </>
                    )
                }

                newConfigItem.filterable = false
            }

            if (type === 'button') {
                newConfigItem.type = 'actions'
                if (configItem.isMultiple?.length > 0) {
                    newConfigItem.getActions = (params) =>
                        configItem.isMultiple.map((action) =>
                            action?.condition && params.id !== 'total-row' ? (
                                actionCondition(params.row, action) ? (
                                    <GridActionsCellItem
                                        key={action.buttonType}
                                        className="tableCellAction"
                                        disabled={
                                            action.disabledType &&
                                            action.disabledType == false
                                                ? !params?.row[
                                                      action.disabledField
                                                  ]
                                                : params?.row[
                                                      action.disabledField
                                                  ]
                                        }
                                        onClick={() =>
                                            action.onclick(
                                                action.clickID
                                                    ? params?.row[
                                                          action.clickID
                                                      ]
                                                    : clickID
                                                      ? params?.row[clickID]
                                                      : params?.row.id,
                                                action.secondaryClickID
                                                    ? params?.row[
                                                          action
                                                              .secondaryClickID
                                                      ]
                                                    : secondaryClickID
                                                      ? params?.row[
                                                            secondaryClickID
                                                        ]
                                                      : null,
                                                action.clickType
                                            )
                                        }
                                        icon={
                                            <span className="material-icons">
                                                {action.buttonType || 'edit'}
                                            </span>
                                        }
                                        showInMenu={action.showInMenu || false}
                                        label={
                                            action.showInMenu &&
                                            action.tooltipTitle
                                                ? action.tooltipTitle
                                                : action.label
                                        }
                                        title={
                                            action.tooltipTitle
                                                ? action.tooltipTitle
                                                : action.label
                                        }
                                    />
                                ) : (
                                    <></>
                                )
                            ) : params.id !== 'total-row' ? (
                                <GridActionsCellItem
                                    key={action.buttonType}
                                    className="tableCellAction"
                                    disabled={
                                        action.disabledType &&
                                        action.disabledType == false
                                            ? !params?.row[action.disabledField]
                                            : params?.row[action.disabledField]
                                    }
                                    onClick={() =>
                                        action.onclick(
                                            action.clickID
                                                ? params?.row[action.clickID]
                                                : clickID
                                                  ? params?.row[clickID]
                                                  : params?.row.id,
                                            action.secondaryClickID
                                                ? params?.row[
                                                      action.secondaryClickID
                                                  ]
                                                : secondaryClickID
                                                  ? params?.row[
                                                        secondaryClickID
                                                    ]
                                                  : null,
                                            action.clickType
                                        )
                                    }
                                    icon={
                                        <span className="material-icons">
                                            {action.buttonType || 'edit'}
                                        </span>
                                    }
                                    showInMenu={action.showInMenu || false}
                                    label={
                                        action.showInMenu && action.tooltipTitle
                                            ? action.tooltipTitle
                                            : action.label
                                    }
                                    title={
                                        action.tooltipTitle
                                            ? action.tooltipTitle
                                            : action.label
                                    }
                                />
                            ) : (
                                <></>
                            )
                        )
                } else {
                    newConfigItem.renderCell = (params) => (
                        <Button
                            key={configItem.field}
                            className="tableCellAction"
                            onClick={() =>
                                configItem.onclick(
                                    clickID
                                        ? params?.row[clickID]
                                        : params?.row.id,
                                    secondaryClickID
                                        ? params?.row[secondaryClickID]
                                        : null,
                                    configItem.clickType
                                )
                            }
                        >
                            <span className="material-icons">
                                {configItem.clickType}
                            </span>
                        </Button>
                    )
                }
            }

            if (type === 'invoiceActive') {
                newConfigItem.renderCell = (params) => {
                    switch (params.value) {
                        case 0:
                            return (
                                <AccessTimeIcon style={{ color: 'orange' }} />
                            )
                        case 1:
                            return (
                                <CheckCircleIcon style={{ color: 'green' }} />
                            )
                        case 2:
                            return <CancelIcon style={{ color: 'red' }} />
                        default:
                            return null
                    }
                }
            }

            if (type === 'capitalize' || newConfigItem.capitalize == true) {
                newConfigItem.renderCell = (params) => capitalized(params.value)
                newConfigItem.valueGetter = (params) =>
                    capitalized(params.value)
            }

            if (type === 'removeUnderscores') {
                newConfigItem.renderCell = (params) =>
                    params.value
                        .split(',')
                        .map((item) =>
                            item
                                .split('_')
                                .map(
                                    (word) =>
                                        word.charAt(0).toUpperCase() +
                                        word.slice(1)
                                )
                                .join(' ')
                        )
                        .join(', ')

                newConfigItem.valueGetter = (params) =>
                    params.value
                        .split(',')
                        .map((item) =>
                            item
                                .split('_')
                                .map(
                                    (word) =>
                                        word.charAt(0).toUpperCase() +
                                        word.slice(1)
                                )
                                .join(' ')
                        )
                        .join(', ')
            }

            if (type === 'hyperlink') {
                newConfigItem.renderCell = (params) =>
                    params.row.id !== 'total-row' && (
                        <span
                            title={params.value}
                            className="dgHyperlink MuiDataGrid-cellContent"
                            onClick={() =>
                                configItem.onclick(
                                    clickID
                                        ? params?.row[clickID]
                                        : params?.row.id,
                                    secondaryClickID
                                        ? params?.row[secondaryClickID]
                                        : params?.row[secondaryClickID],
                                    configItem.clickType
                                )
                            }
                        >
                            {params.value}
                        </span>
                    )
            }

            if (type === 'or_fields') {
                newConfigItem.renderCell = (params) => (
                    <span
                        title={
                            params?.row[configItem?.field]
                                ? params?.row[configItem?.field]
                                : params?.row[configItem?.custom] ||
                                  params.value
                        }
                        className="dgHyperlink MuiDataGrid-cellContent"
                        onClick={() =>
                            configItem.onclick(
                                clickID ? params?.row[clickID] : params?.row.id,
                                secondaryClickID
                                    ? params?.row[secondaryClickID]
                                    : params?.row[secondaryClickID],
                                configItem.clickType
                            )
                        }
                    >
                        {params?.row[configItem?.field]
                            ? params?.row[configItem?.field]
                            : params?.row[configItem?.custom] || params.value}
                    </span>
                )
            }

            if (type === 'customLabelColor') {
                newConfigItem.renderCell = (params) => (
                    <span
                        title={params.value}
                        className=" MuiDataGrid-cellContent"
                        style={{
                            color: params.value
                                ? params?.row[configItem?.labelColorFieldCheck]
                                    ? configItem?.successColor
                                    : configItem?.failColor
                                : '',
                        }}
                    >
                        {params.value}
                    </span>
                )
            }
            if (addStarMark) {
                newConfigItem.renderCell = (params) => (
                    <span
                        title={params.value}
                        className="MuiDataGrid-cellContent"
                    >
                        {configItem?.custom &&
                        params?.row[configItem?.custom] ? (
                            <>
                                {params.value}
                                <span
                                    style={{
                                        fontSize: '1.5em',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {' '}
                                    *
                                </span>
                            </>
                        ) : (
                            params.value
                        )}
                    </span>
                )
            }

            if (field.includes(',')) {
                newConfigItem.renderCell = (params) => (
                    <div className="MuiDataGrid-cellContent">
                        {field
                            .split(',')
                            .map((value) => params?.row[value] || '')
                            .join(' ')}
                    </div>
                )

                newConfigItem.valueGetter = (params) =>
                    field
                        .split(',')
                        .map((value) => params?.row[value] || '')
                        .join(' ')
            }

            if (type === 'date') {
                newConfigItem.valueFormatter = (params) =>
                    params.value && moment(params.value).isValid()
                        ? moment(params.value).format(
                              global.dateFormat
                                  ? global.dateFormat
                                  : 'MM/DD/YYYY'
                          )
                        : ''

                newConfigItem.valueGetter = (params) =>
                    params.value && moment(params.value).isValid()
                        ? moment(params.value).format(
                              global.dateFormat
                                  ? global.dateFormat
                                  : 'MM/DD/YYYY'
                          )
                        : ''
            }

            if (type === 'html') {
                newConfigItem.valueFormatter = (params) =>
                    params.value
                        ? params.value
                              .replace(/<[^>]+>/g, '') // Remove HTML tags
                              .replace(/&nbsp;/g, ' ') // Replace &nbsp; with a single space
                              .replace(/\s+/g, ' ') // Collapse multiple spaces into one
                              .trim() // Remove leading/trailing spaces
                        : ''
                newConfigItem.valueGetter = (params) =>
                    params.value
                        ? params.value
                              .replace(/<[^>]+>/g, '') // Remove HTML tags
                              .replace(/&nbsp;/g, ' ') // Replace &nbsp; with a single space
                              .replace(/\s+/g, ' ') // Collapse multiple spaces into one
                              .trim() // Remove leading/trailing spaces
                        : ''
            }

            if (type === 'condition') {
                newConfigItem.renderCell = (params) =>
                    conditionalFormatting(params.row, params.colDef)
                newConfigItem.valueGetter = (params) =>
                    conditionalFormatting(params.row, params.colDef)
            }

            if (headerName === 'Action') {
                newConfigItem.headerAlign = 'center'
                newConfigItem.align = 'center'
            }

            if (type === 'datetime') {
                newConfigItem.type = 'dateTime'
                newConfigItem.valueFormatter = (params) =>
                    params?.value && moment(params.value).isValid()
                        ? moment(params?.value).format(
                              global?.defaultCSVDateTimeFormat
                                  ? global.defaultCSVDateTimeFormat
                                  : global?.defaultDateTimeFormat
                                    ? global.defaultDateTimeFormat
                                    : 'MM/DD/YYYY hh:mm A'
                          )
                        : ''
                newConfigItem.valueGetter = ({ value }) =>
                    value && new Date(value)
            }

            if (type === 'datetimesecond') {
                newConfigItem.type = 'dateTime'
                newConfigItem.valueFormatter = (params) =>
                    params.value && moment(params.value).isValid()
                        ? moment(params?.value).format(
                              global?.defaultDateTimeFormat
                                  ? global.defaultDateTimeFormat
                                  : 'MM/DD/YYYY hh:mm:ss A'
                          )
                        : ''
                newConfigItem.valueGetter = ({ value }) =>
                    value && new Date(value)
            }

            if (field === 'bidscount') {
                newConfigItem.type = 'number'
            }
            if (type === 'price') {
                newConfigItem.type = 'number'
                newConfigItem.valueGetter = ({ value }) =>
                    value ? currencyFormat(value) : ''
            }
            if (type === 'countryprice') {
                newConfigItem.type = 'number'
                newConfigItem.valueGetter = (params) => {
                    const price = params.value
                    const country = params.row[countryfield] // Adjust to match your field name
                    return price ? currencyFormat(price, country) : ''
                }
            }

            if (type === 'tooltip') {
                newConfigItem.renderCell = renderTooltipCell(
                    configItem,
                    clickID,
                    secondaryClickID
                )

                newConfigItem.valueGetter = (params) => params.value
            }

            if (type === 'percentage') {
                newConfigItem.type = 'string'
                newConfigItem.valueGetter = ({ value }) =>
                    value && parseFloat(value, 10) + '%'
            }

            if (type === 'decimal_percentage') {
                newConfigItem.type = 'string'
                newConfigItem.valueGetter = ({ value }) =>
                    value && parseFloat(value, 10).toFixed(2) + '%'
            }

            if (type === 'decimal_percentage_three') {
                newConfigItem.type = 'string'
                newConfigItem.valueGetter = ({ value }) =>
                    value && parseFloat(value, 10).toFixed(3) + '%'
            }

            if (type === 'decimal_three') {
                newConfigItem.type = 'number'
                newConfigItem.valueGetter = ({ value }) =>
                    value && parseFloat(value, 10).toFixed(3)
            }

            if (type === 'statusIcon') {
                newConfigItem.renderCell = (params) => (
                    <Tooltip title={tooltipTitle || ''} arrow>
                        {params.value == 0 ? (
                            <span className="material-icons dgStatusIcon inactive">
                                cancel
                            </span>
                        ) : (
                            <span className="material-icons dgStatusIcon active">
                                check_circle
                            </span>
                        )}
                    </Tooltip>
                )
                newConfigItem.filterable = false
            }

            if (field === 'action') {
                newConfigItem.minWidth = actionsWidth
            } else if (type === 'imagewithurl') {
                newConfigItem.minWidth = 60
            } else if (type === 'multiimagewithurl') {
                newConfigItem.minWidth = 60
            } else if (
                field === 'title' ||
                field === 'projects_title' ||
                field === 'email' ||
                field === 'phone'
            ) {
                newConfigItem.flex = 1
                newConfigItem.width = 200
                newConfigItem.minWidth = 200
            } else {
                newConfigItem.flex = 1
                newConfigItem.width = width ?? props?.width ?? 100
                newConfigItem.minWidth = width ?? props?.width ?? 100
            }

            if (field === 'id') {
                newConfigItem.valueGetter = ({ value }) =>
                    value && value === 'total-row' ? '' : value
            }

            if (type === 'boolean') {
                newConfigItem.renderCell = ({ value, row }) => {
                    if (row.id === 'total-row') {
                        return null
                    }

                    return value ? <span>✔️</span> : <span>❌</span>
                }
            }

            newConfigItem.editable = editable || false

            newConfigItem.filterable = filterable || false
            newConfigItem.summary = summary || false
            newConfigItem.summaryType = summaryType || ''
            newConfigItem.filterField = filterField || ''
            newConfigItem.summaryField = summaryField || ''
            newConfigItem.sortField = sortField || ''
            newConfigItem.valueOptions = valueOptions || []

            outputConfig.push(newConfigItem)
        })

        return outputConfig
    }

    useEffect(() => {
        setRowSelectionModel([])
        if (props.searchData) {
            propsSelectData(props.searchData)
        } else {
            setRowSelectionModel([])
        }
        return () => {
            setRowSelectionModel([])
        }
    }, [props.tableBody])

    useEffect(() => {
        if (props.searchData) {
            propsSelectData(props.searchData)
        }
    }, [props.searchData])

    useEffect(() => {
        if (props.selectedUpdate) {
            props.selectedUpdate(rowSelectionModel)
        }
    }, [rowSelectionModel])

    const propsSelectData = (selectData) => {
        let values = selectData
        if (values.length) {
            const newSelecteds = []
            props.tableBody.map((item) => {
                if (values.includes(item.id)) {
                    newSelecteds.push(item.id)
                }
            })

            setRowSelectionModel(newSelecteds)
        } else {
            setRowSelectionModel([])
        }
    }

    const alignTableValues = (tableValues = []) => {
        const actionItem = tableValues.find((item) => item.field === 'action')

        if (!actionItem) return tableValues

        return [
            { ...actionItem, tableShow: 1, className: 'sticky' },
            ...tableValues.filter((item) => item.field !== 'action'),
        ]
    }
    const align = props?.alignAction ?? global?.tableActionAlign

    const columns =
        align === 'left'
            ? convertConfig(alignTableValues(props.tableValues))
            : convertConfig(props.tableValues)

    const handleSortModelChange = React.useCallback(
        (event) => {
            let tempColumns = columns
            if (event.length) {
                let sortFilteredField = ''
                let filteredValue = tempColumns.filter(
                    (el) => el.field === event[0].field
                )
                if (filteredValue.length) {
                    if (filteredValue[0].sortField) {
                        sortFilteredField = filteredValue[0].sortField
                    } else if (filteredValue[0].filterField) {
                        sortFilteredField = filteredValue[0].filterField
                    }
                }

                formik.order = event[0].sort
                formik.orderby = sortFilteredField || event[0].field
                changeTableFunction(
                    formik,
                    props.fromVariable ? props.fromVariable : ''
                )
            } else {
                formik.order = ''
                if (props?.filterable) {
                    formik.orderby = ''
                }
                changeTableFunction(
                    formik,
                    props.fromVariable ? props.fromVariable : ''
                )
            }
        },
        [columns]
    )

    const handleSelectionModel = (newRowSelectionModel) => {
        if (props.selectedRowCount) {
            props.selectedRowCount(newRowSelectionModel)
        }
        setRowSelectionModel(newRowSelectionModel)
    }

    // const [queryOptions, setQueryOptions] = React.useState({})
    const onFilterChange = React.useCallback(
        (model) => {
            let tempColumns = columns
            const filterModel = JSON.parse(JSON.stringify(model))

            filterModel.items.map((value) => {
                value.filterField = value.field
                let filteredValue = tempColumns.filter(
                    (el) => el.field === value.field
                )
                if (filteredValue.length) {
                    if (filteredValue[0].filterField) {
                        value.filterField = filteredValue[0].filterField
                    }
                    value.type = filteredValue[0].type

                    if (filteredValue[0].type == 'dateTime') {
                        value.type =
                            filteredValue[0].type === 'dateTime'
                                ? 'datetime'
                                : filteredValue[0].type
                        value.value = value.value
                            ? moment(value.value).isValid()
                                ? moment(
                                      value.value,
                                      'YYYY-MM-DDTh:mm'
                                  ).format()
                                : null
                            : null
                    } else if (filteredValue[0].type == 'boolean') {
                        value.value =
                            value.value == 'true'
                                ? 1
                                : value.value == 'false'
                                  ? '0'
                                  : ''
                    }
                }
                return value
            })
            formik.datagridfilters = filterModel
            changeTableFunction(
                formik,
                props.fromVariable ? props.fromVariable : ''
            )
        },
        [columns]
    )

    const getRowId = (row) => {
        if (props.diff_id) {
            return row[props.diff_id]
        } else {
            return row.id
        }
    }

    const [paginationModel, setPaginationModel] = React.useState({
        pageSize: 5,
        page: 0,
    })

    const [columnOrder, setColumnOrder] = useState(null)

    useEffect(() => {
        if (props.columnStorageVariable) {
            const savedColumnOrder = localStorage.getItem(
                props.columnStorageVariable
            )
            if (savedColumnOrder) {
                setColumnOrder(JSON.parse(savedColumnOrder))
            }
        }
    }, [props.columnStorageVariable])

    useEffect(() => {
        if (props.columnStorageVariable) {
            if (columnOrder) {
                localStorage.setItem(
                    props.columnStorageVariable,
                    JSON.stringify(columnOrder)
                )
            }
        }
    }, [props.columnStorageVariable, columnOrder])

    const [editRowsModel, setEditRowsModel] = useState({})

    const handleEditRowModelChange = (newModel) => {
        setEditRowsModel(newModel)
    }

    const handleEditCommit = (params) => {
        const editedRow = rows.find((row) => row.id === params.id)
        // Perform API call to update data on the server
        // Replace the following lines with your actual API call implementation
    }

    const mutateRow = useFakeMutation()
    const noButtonRef = React.useRef(null)
    const [promiseArguments, setPromiseArguments] = React.useState(null)

    const [snackbar, setSnackbar] = React.useState(null)

    const handleCloseSnackbar = () => setSnackbar(null)

    const processRowUpdate = React.useCallback(
        (newRow, oldRow) =>
            new Promise((resolve, reject) => {
                const mutation = computeMutation(newRow, oldRow)

                if (mutation && !mutation.includes('undefined')) {
                    // Save the arguments to resolve or reject the promise later
                    setPromiseArguments({ resolve, reject, newRow, oldRow })
                } else {
                    resolve(oldRow) // Nothing was changed
                }
            }),
        []
    )

    const handleNo = () => {
        const { oldRow, resolve } = promiseArguments
        resolve(oldRow) // Resolve with the old row to not update the internal state
        setPromiseArguments(null)
    }

    const handleYes = async () => {
        const { newRow, oldRow, reject, resolve } = promiseArguments

        try {
            const updatedField = Object.keys(newRow).find(
                (key) => newRow[key] !== oldRow[key]
            )

            let updateValue = {}

            updateValue['id'] = newRow.id
            updateValue[updatedField] = newRow[updatedField]

            props.editFunction(updateValue)

            // Make the HTTP request to save in the backend
            const response = await mutateRow(newRow)

            resolve(response)
            setPromiseArguments(null)
        } catch (error) {
            console.log(error, 'errormessage')
            setSnackbar({ children: "Name can't be empty", severity: 'error' })
            reject(oldRow)
            setPromiseArguments(null)
        }
    }

    const handleEntered = () => {
        // The `autoFocus` is not used because, if used, the same Enter that saves
        // the cell triggers "No". Instead, we manually focus the "No" button once
        // the dialog is fully open.
        // noButtonRef.current?.focus();
    }

    const changeRowClass = (params) => {
        if (params.row.id === 'total-row') {
            return `total-row`
        }
        if (props.changeRowClass && props.changeClassField) {
            if (params.row[props.changeClassField]) {
                if (
                    props.classValue &&
                    params.row[props.changeClassField] == props.initialValue
                ) {
                    return `MuiDataGrid-row ${props.classValue}`
                } else {
                    return `MuiDataGrid-row ${
                        params.row[props.changeClassField]
                    }`
                }
            }
        } else {
            return `MuiDataGrid-row`
        }
    }

    const changeCellClass = (params) => {
        if (params.row.id === 'total-row') {
            return 'total-cell'
        }
        return ''
    }

    const renderConfirmDialog = () => {
        if (!promiseArguments) {
            return null
        }

        const { newRow, oldRow } = promiseArguments
        const mutation = computeMutation(newRow, oldRow)
        if (global?.noConfirm) {
            handleYes()
        } else {
            return (
                <Dialog
                    maxWidth="xs"
                    TransitionProps={{ onEntered: handleEntered }}
                    open={!!promiseArguments}
                >
                    <DialogTitle>Are you sure?</DialogTitle>
                    <DialogContent dividers>
                        {`Pressing 'Yes' will change ${mutation}.`}
                    </DialogContent>
                    <DialogActions>
                        <Button ref={noButtonRef} onClick={handleNo}>
                            No
                        </Button>
                        <Button onClick={handleYes}>Yes</Button>
                    </DialogActions>
                </Dialog>
            )
        }
    }

    const [displayRows, setDisplayRows] = useState([])

    const updateDisplayRows = (props) => {
        const apiSummary = props.allData?.summary || {}
        const rows = props.tableBody || []
        const totals = calculateSummary(columns, apiSummary)
        if (totals && apiSummary) {
            const totalsRow = {
                id: 'total-row',
                title: 'Total',
                ...totals,
            }
            setDisplayRows([...rows, ...[totalsRow]])
        } else {
            setDisplayRows(rows)
        }
    }

    useEffect(() => {
        if (
            formik?.summaryFields &&
            Object.keys(formik?.summaryFields)?.length
        ) {
            updateDisplayRows(props, formik)
        } else {
            setDisplayRows(props.tableBody)
        }
    }, [props.tableBody, formik.page, formik.limit])

    return (
        <div className="dataGridCnt">
            {renderConfirmDialog()}
            <DataGrid
                disableRowSelectionOnClick
                checkboxSelection={props.tableActions?.length ? true : false}
                isRowSelectable={(params) => params.row.id !== 'total-row'}
                loading={props.isLoading}
                rows={displayRows}
                columns={columns}
                columnVisibilityModel={
                    columnOrder || props.columnVisibilityModel || {}
                }
                onColumnVisibilityModelChange={(newOrder) =>
                    setColumnOrder(newOrder)
                }
                // paginationModel={{
                //     pageSize: formik.limit,
                //     page: formik.page - 1,
                // }}
                onRowSelectionModelChange={handleSelectionModel}
                rowSelectionModel={rowSelectionModel}
                paginationModel={
                    props.paginationMode === 'client'
                        ? paginationModel
                        : {
                              pageSize: formik.limit,
                              page: formik.page - 1,
                          }
                }
                pageSizeOptions={
                    global.limitOptions
                        ? global.limitOptions
                        : formik.limitOptions
                          ? formik.limitOptions
                          : [
                                ...new Set([5, 20, 40, 60, 100, formik.limit]),
                            ].sort((a, b) => a - b)
                }
                sortingMode={`${props.sortingMode || 'server'}`}
                onSortModelChange={
                    props.sortingMode === 'client'
                        ? null
                        : handleSortModelChange
                }
                paginationMode={props.paginationMode || 'server'}
                onPaginationModelChange={
                    props.paginationMode === 'client'
                        ? handlePageChange
                        : handleChangePage
                }
                rowCount={
                    props.paginationMode === 'client'
                        ? props.tableBody.length
                        : props.allData.totalRecords
                }
                filterMode={`${props.filterMode || 'server'}`}
                onFilterModelChange={
                    props.filterMode === 'client' ? null : onFilterChange
                }
                getRowId={getRowId}
                getRowClassName={(params) => changeRowClass(params)}
                getCellClassName={(params) => changeCellClass(params)}
                // selectionModel={selectionModel}
                // onSelectionModelChange={handleSelectionModelChange}
                // components={{
                //     toolbar: CustomToolbar,
                // }}

                // editRowsModel={editRowsModel}
                // onEditRowModelChange={handleEditRowModelChange}
                // onEditCellChangeCommitted={handleEditCommit}
                slots={{
                    toolbar: CustomToolbar,
                    noRowsOverlay: NoRecords,
                }}
                slotProps={{
                    toolbar: { props },
                    filterPanel: {
                        columnsSort: 'asc',
                        filterFormProps: {
                            logicOperatorInputProps: {
                                variant: 'outlined',
                                size: 'small',
                            },
                            columnInputProps: {
                                variant: 'outlined',
                                size: 'small',
                            },
                            operatorInputProps: {
                                variant: 'outlined',
                                size: 'small',
                            },
                            valueInputProps: {
                                InputComponentProps: {
                                    variant: 'outlined',
                                    size: 'small',
                                },
                            },
                            deleteIconProps: {
                                sx: {
                                    '& .MuiSvgIcon-root': { color: '#d32f2f' },
                                },
                            },
                        },
                        sx: {
                            '& .MuiDataGrid-filterForm': { p: 2 },
                            '& .MuiDataGrid-filterForm:nth-child(even)': {
                                backgroundColor: (theme) =>
                                    theme.palette.mode === 'dark'
                                        ? '#444'
                                        : '#f5f5f5',
                            },
                            '& .MuiDataGrid-filterFormLogicOperatorInput': {
                                mr: 2,
                            },
                            '& .MuiDataGrid-filterFormColumnInput': {
                                mr: 2,
                                width: 150,
                            },
                            '& .MuiDataGrid-filterFormOperatorInput': { mr: 2 },
                            '& .MuiDataGrid-filterFormValueInput': {
                                width: 200,
                            },
                        },
                    },
                }}
                processRowUpdate={processRowUpdate}
            />
            {!!snackbar && (
                <Snackbar
                    open
                    onClose={handleCloseSnackbar}
                    autoHideDuration={6000}
                >
                    <Alert {...snackbar} onClose={handleCloseSnackbar} />
                </Snackbar>
            )}
        </div>
    )
}

export default CustomDataGrid
